import type { FC } from 'react';
import {
  MAX_LENGTH_CUSTOMER_REFERENCE,
  MAX_LENGTH_LOADING_NOTE,
} from '../../../../../common/fieldLengths';
import type { BasicBooking } from '../../../../../common/graphql/fragments/gql/BasicBooking';
import { useAllCustomersAsOptions } from '../../../../../common/graphql/useAllCustomersAsOptions';
import { useLoadingPreferenceOptions } from '../../../../../common/graphql/useLoadingPreferenceOptions';
import { useUpdateRegistrationNumberAlertApplied } from '../../../../../common/registrationNumberAlert/hooks/useUpdateRegistrationNumberAlertApplied';
import { type BookingFields, isHidden, isVisible } from '../../../../../common/utils/bookingFields';
import { customerReferencePlaceHolder } from '../../../../../common/utils/customerUtils';
import { type GoodsApproval, GoodsApprovalType } from '../../../../../common/utils/GoodsApproval';
import { resolveLoadingPreferenceApproved } from '../../../../../common/utils/loadingPreferenceUtil';
import {
  transformCustomer,
  transformLoadingPreferenceOption,
} from '../../../../../common/utils/optionTransformers';
import { DetailsFieldCell } from '../../../../../common-ui/details/DetailsFieldCell';
import { HiddenFieldGate } from '../../../../../common-ui/HiddenFieldGate';
import { ColumnId } from '../../../../../gql/graphql';
import type { BookingFormState } from '../../../../../services/update-form/updateFormReducer';
import { DropdownField } from '../fields/DropdownField';
import { TextInputField } from '../fields/TextInputField';
import { BookingSection } from './BookingSection';

interface LoadingPreferenceCustomerSectionProps {
  booking: BasicBooking;
  disabled: boolean;
  fieldsToEnable: BookingFields;
  fieldsToShow: BookingFields;
  formState: BookingFormState;
}

export const LoadingDetailsSection: FC<LoadingPreferenceCustomerSectionProps> = ({
  disabled,
  fieldsToShow,
  fieldsToEnable,
  formState,
  booking,
}) => {
  const loadingPreferenceApproval: GoodsApproval = {
    status: resolveLoadingPreferenceApproved(
      booking.loadingPreference,
      booking.loadingPreferenceStatusCode,
    ),
    type: GoodsApprovalType.LoadingPreference,
  };

  const customerOptions = useAllCustomersAsOptions({ includeClosedCustomers: true });

  const showAccount = customerOptions.length > 1 && isVisible(fieldsToShow, ColumnId.CustIndex);

  const isDisabled = (field: ColumnId) => disabled || isHidden(fieldsToEnable, field);

  const { icon, arrivalNotepadApplied } = useUpdateRegistrationNumberAlertApplied(
    'Details',
    'loadingNote',
    undefined,
  );
  const [loadingPreferenceOptions] = useLoadingPreferenceOptions(formState?.route?.code);

  return (
    <BookingSection title={'Details'}>
      <HiddenFieldGate hidden={!showAccount}>
        <DetailsFieldCell
          label="Account"
          className={'t_account_details_edit'}
          renderField={id => (
            <DropdownField
              trackerCategory={'Details'}
              name={'customer'}
              transformOption={transformCustomer}
              disabled={isDisabled(ColumnId.CustIndex)}
              options={customerOptions}
              id={id}
              filterOption={option => option.data.data.isValid}
            />
          )}
        />
      </HiddenFieldGate>
      <HiddenFieldGate hidden={isHidden(fieldsToShow, ColumnId.CustomerReference)}>
        <DetailsFieldCell
          label="Customer reference"
          renderField={id => (
            <TextInputField
              id={id}
              trackerCategory={'Details'}
              name={'customerReference'}
              maxLength={MAX_LENGTH_CUSTOMER_REFERENCE}
              disabled={isDisabled(ColumnId.CustomerReference)}
              placeholder={customerReferencePlaceHolder(formState.customer)}
            />
          )}
        />
      </HiddenFieldGate>
      <HiddenFieldGate hidden={isHidden(fieldsToShow, ColumnId.LoadingListMessage)}>
        <DetailsFieldCell
          label="Loading note"
          renderField={id => (
            <TextInputField
              id={id}
              trackerCategory={'Details'}
              disabled={arrivalNotepadApplied || isDisabled(ColumnId.LoadingListMessage)}
              name={'loadingListMessage'}
              maxLength={MAX_LENGTH_LOADING_NOTE}
              buttonRight={icon}
            />
          )}
        />
      </HiddenFieldGate>
      <HiddenFieldGate hidden={isHidden(fieldsToShow, ColumnId.LoadingPreferenceCode)}>
        <DetailsFieldCell
          label="Loading preference"
          renderField={id => (
            <DropdownField
              id={id}
              trackerCategory={'Details'}
              name={'loadingPreference'}
              transformOption={transformLoadingPreferenceOption}
              disabled={isDisabled(ColumnId.LoadingPreferenceCode)}
              options={loadingPreferenceOptions}
            />
          )}
          approved={loadingPreferenceApproval}
          flex={1}
        />
      </HiddenFieldGate>
    </BookingSection>
  );
};
